import { UTCDeliveryPeriod } from '@epicuro-next/ataraxia/models';
import { ExtendedBasketAccountAddress } from '@epicuro-next/state/basket-state';
import { makeIntervalISODate } from '@epicuro-next/utilities';
import { OrderType } from 'graphql-api/gql-api-interfaces';

import { isTrustedDeliveryPeriod } from '../../../actions';
import {
  BulkTypeDeliveryPeriod,
  BusinessState,
  DeliveryPeriodType,
  UiState,
} from '../../../models';
import { getDeliveryShopKey } from '../../../shared/shared';
import { updateDeliveryPeriodsCacheReducer } from '../cache-delivery-periods/update-delivery-periods-cache.reducer';

export function addBulkDeliveryPeriodBusiness(
  state: BusinessState,
  {
    deliveryPeriod: { from, to },
    deliveryShops,
  }: {
    deliveryPeriod: UTCDeliveryPeriod & isTrustedDeliveryPeriod;
    deliveryShops: ExtendedBasketAccountAddress[];
  },
): BusinessState {
  if (!from || !to) {
    return state;
  }

  const fromJson = from;
  const toJson = to;

  // new bulk delivery period
  const selectedDeliveryPeriod = {
    from: fromJson,
    to: toJson,
  };

  const deliveryShopsDeliveryPeriods = deliveryShops
    ?.map((deliveryShop) => {
      const deliveryShopId = getDeliveryShopKey(deliveryShop);
      const deliveryPeriodsByShop = state.deliveryPeriods[deliveryShopId] ?? {};
      const deliveryPeriodsByType =
        deliveryPeriodsByShop[DeliveryPeriodType.Bulk] ?? {};
      const shopAndTypeDeliveryPeriods =
        deliveryPeriodsByType.deliveryPeriods ?? [];

      const exists = shopAndTypeDeliveryPeriods?.find(
        ({ from: existingFrom, to: existingTo }: BulkTypeDeliveryPeriod) =>
          fromJson === existingFrom && toJson === existingTo,
      );

      if (!!exists) {
        return {
          [deliveryShopId]: deliveryPeriodsByShop,
        };
      }

      return {
        [deliveryShopId]: {
          ...deliveryPeriodsByShop,
          currentDeliveryPeriodType: DeliveryPeriodType.Bulk,
          [DeliveryPeriodType.Bulk]: {
            ...deliveryPeriodsByType,
            deliveryPeriods: [
              ...shopAndTypeDeliveryPeriods,
              selectedDeliveryPeriod,
            ],
          },
        },
      };
    })
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});

  return {
    ...state,
    deliveryPeriods: {
      ...deliveryShopsDeliveryPeriods,
    },
    cached: updateDeliveryPeriodsCacheReducer(
      state.cached,
      makeIntervalISODate(fromJson, toJson),
    ),
  };
}

export function addBulkDeliveryPeriodUi(
  state: UiState,
  {
    deliveryPeriod: { from, to, isTrusted },
  }: {
    deliveryPeriod: UTCDeliveryPeriod & isTrustedDeliveryPeriod;
  },
): UiState {
  if (!from || !to) {
    return state;
  }

  return {
    ...state,
    selectedOrderType: !!isTrusted ? OrderType.Future : state.selectedOrderType,
  };
}
